import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { apiArrivedPackageDeliver } from '@/entities/warehouse-arrivals';

type Props = {
  packageId: string;
  value: boolean;
  canEdit: boolean;
};

export const DeliveredToggle = ({ packageId, value, canEdit }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [pending, setPending] = useState<boolean | null>(null);

  const mutation = useMutate({
    api: (next: boolean) => apiArrivedPackageDeliver(packageId, { delivered_to_customer: next }),
    invalidateKeys: [queryKeys.warehouseArrivals.all, queryKeys.warehouse.all, queryKeys.parcels.all],
    onSuccess: () => {
      toast.success(t('warehouseArrivals.packages.toast.delivered'));
      setPending(null);
    },
    onError: (_e, message) => toast.error(message),
  });

  if (canEdit) {
    return (
      <>
        <label onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            className="h-4 w-4 cursor-pointer accent-brand-600 disabled:cursor-not-allowed"
            checked={value}
            disabled={mutation.isPending}
            onChange={(e) => {
              e.stopPropagation();
              setPending(e.target.checked);
            }}
            aria-label={t('warehouseArrivals.packages.deliveredToCustomer')}
          />
        </label>
        <ConfirmDialog
          open={pending !== null}
          title={
            pending === false
              ? t('warehouseArrivals.packages.confirm.cancelTitle')
              : t('warehouseArrivals.packages.confirm.markTitle')
          }
          description={
            pending === false
              ? t('warehouseArrivals.packages.confirm.cancelDescription')
              : t('warehouseArrivals.packages.confirm.markDescription')
          }
          confirmLabel={
            pending === false
              ? t('warehouseArrivals.packages.confirm.cancelConfirm')
              : t('warehouseArrivals.packages.confirm.markConfirm')
          }
          cancelLabel={t('warehouseArrivals.packages.confirm.dismiss')}
          confirmVariant={pending === false ? 'danger' : 'primary'}
          loading={mutation.isPending}
          onConfirm={() => {
            if (pending !== null) mutation.mutate(pending);
          }}
          onCancel={() => {
            if (!mutation.isPending) setPending(null);
          }}
        />
      </>
    );
  }

  return (
    <span
      className={
        value
          ? 'rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-100 text-emerald-800'
          : 'rounded-full px-2.5 py-0.5 text-xs font-semibold bg-slate-100 text-slate-600'
      }
    >
      {value
        ? t('warehouseArrivals.packages.delivered.yes')
        : t('warehouseArrivals.packages.delivered.no')}
    </span>
  );
};
